import { useContext, useEffect, useState, useMemo } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Input, Select, Option, Button,
    IconButton, Divider, Textarea,
} from '@mui/joy';

import CreditCardRoundedIcon       from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon         from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon      from '@mui/icons-material/PointOfSaleRounded';
import AddRoundedIcon              from '@mui/icons-material/AddRounded';
import ReceiptLongRoundedIcon      from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon    from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon       from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon       from '@mui/icons-material/LocalOfferRounded';
import PhoneIphoneRoundedIcon      from '@mui/icons-material/PhoneIphoneRounded';

import { AppContext } from '../../context';
import {
    APIGetEmployees,
    APICreateOrder,
    APIGetDayAssignments,
    APIGetProducts,
    APIGetDiscounts,
    APIGetPaymentNumbers,
    parseApiError,
} from '../../api';

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    color: 'var(--joy-palette-text-primary)',
    fontSize: 13,
    '&:hover, &:focus-within': {
        borderColor: 'var(--joy-palette-primary-500)',
        bgcolor: 'var(--joy-palette-background-level2)',
    },
};

const MONEY_TYPES = [
    { v: 'transfer', label: 'Перевод',  icon: <CreditCardRoundedIcon sx={{ fontSize: 14 }} />, c: '#818cf8' },
    { v: 'cash',     label: 'Наличные', icon: <PaymentsRoundedIcon sx={{ fontSize: 14 }} />,   c: '#22c55e' },
    { v: 'terminal', label: 'Терминал', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />, c: '#f59e0b' },
];

const emptyLine = () => ({
    money_type: 'transfer',
    items: [{ product_id: null, quantity: 1, unit_price: 0, comment: '' }],
    discounts: [],
});

export default function WorkstationAddOrderModal({ open, onClose, onCreated, locationId }) {
    const { token, showToast } = useContext(AppContext);

    const [photographers, setPhotographers] = useState([]);
    const [loadingPh,     setLoadingPh]     = useState(false);

    const [phId,            setPhId]            = useState('');
    const [lines,           setLines]           = useState([emptyLine()]);
    const [comment,         setComment]         = useState('');
    const [paymentNumberId, setPaymentNumberId] = useState('');
    const [submitting,      setSubmitting]      = useState(false);

    const [products,       setProducts]       = useState([]);
    const [discounts,      setDiscounts]      = useState([]);
    const [paymentNumbers, setPaymentNumbers] = useState([]);

    // ── Загрузка справочников и фотографов при открытии ──────
    useEffect(() => {
        if (!open || !token) return;
        let cancelled = false;
        (async () => {
            setLoadingPh(true);
            const today = new Date();
            const iso = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

            const [all, assignments, prods, discs, pNums] = await Promise.all([
                APIGetEmployees(token, 'can_have_orders'),
                APIGetDayAssignments(token, iso),
                APIGetProducts(token, { is_active: true }),
                APIGetDiscounts(token, { is_active: true }),
                APIGetPaymentNumbers(token, { is_active: true }),
            ]);

            if (cancelled) return;

            if (Array.isArray(prods)) setProducts(prods);
            if (Array.isArray(discs)) setDiscounts(discs);
            // Номера: показываем общие (location=null) + для текущей точки
            if (Array.isArray(pNums)) {
                const filtered = pNums.filter(p =>
                    !p.location || p.location === locationId
                );
                setPaymentNumbers(filtered);
            }

            const assignedIds = new Set(
                (Array.isArray(assignments) ? assignments : []).map(a => a.account)
            );

            if (Array.isArray(all)) {
                // Только активные + назначенные на сегодня
                setPhotographers(
                    all.filter(e =>
                        !e.is_blocked && !e.is_fired &&
                        assignedIds.has(e.id)
                    )
                );
            }
            setLoadingPh(false);
        })();
        return () => { cancelled = true; };
    }, [open, token]);

    // ── Сброс формы при закрытии ─────────────────────────────
    useEffect(() => {
        if (!open) {
            setPhId('');
            setLines([{ value: '', money_type: 'transfer' }]);
            setSubmitting(false);
        }
    }, [open]);

    // Сброс при открытии/закрытии
    useEffect(() => {
        if (!open) {
            setPhId('');
            setLines([emptyLine()]);
            setComment('');
            setPaymentNumberId('');
        }
    }, [open]);

    // ── Строки оплаты ─────────────────────────────────────────────
    const addLine    = () => setLines(prev => [...prev, emptyLine()]);
    const removeLine = (idx) => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    const updateLine = (idx, field, val) => setLines(prev =>
        prev.map((l, i) => i === idx ? { ...l, [field]: val } : l));

    // ── Позиции (товары/услуги) ───────────────────────────────────
    const addItem = (lineIdx) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, items: [...l.items, { product_id: null, quantity: 1, unit_price: 0, comment: '' }] }
        : l));
    const removeItem = (lineIdx, itemIdx) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, items: l.items.filter((_, k) => k !== itemIdx) }
        : l));
    const updateItem = (lineIdx, itemIdx, field, val) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, items: l.items.map((it, k) => k === itemIdx ? { ...it, [field]: val } : it) }
        : l));
    const pickProduct = (lineIdx, itemIdx, productId) => {
        const product = products.find(p => p.id === productId);
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, items: l.items.map((it, k) => k === itemIdx
                ? { ...it, product_id: productId, unit_price: product ? parseFloat(product.price) : it.unit_price }
                : it) }
            : l));
    };

    // ── Скидки ────────────────────────────────────────────────────
    const addDiscount = (lineIdx) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, discounts: [...l.discounts, { discount_id: null, value: 0, is_percent: false, order_item_index: null, name: '' }] }
        : l));
    const removeDiscount = (lineIdx, dIdx) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, discounts: l.discounts.filter((_, k) => k !== dIdx) }
        : l));
    const updateDiscount = (lineIdx, dIdx, field, val) => setLines(prev => prev.map((l, i) => i === lineIdx
        ? { ...l, discounts: l.discounts.map((d, k) => k === dIdx ? { ...d, [field]: val } : d) }
        : l));
    const pickDiscount = (lineIdx, dIdx, discountId) => {
        const discount = discounts.find(d => d.id === discountId);
        setLines(prev => prev.map((l, i) => i === lineIdx
            ? { ...l, discounts: l.discounts.map((d, k) => k === dIdx
                ? { ...d, discount_id: discountId, is_percent: discount?.is_percent || false,
                          value: discount ? parseFloat(discount.value_from) : d.value }
                : d) }
            : l));
    };

    // ── Итоги ─────────────────────────────────────────────────────
    const lineTotal = (line) => {
        const subtotal = line.items.reduce((s, it) => s + (it.unit_price * it.quantity), 0);
        const discountSum = line.discounts.reduce((s, d) => {
            if (d.is_percent) {
                const base = d.order_item_index !== null
                    ? (line.items[d.order_item_index]?.unit_price * line.items[d.order_item_index]?.quantity) || 0
                    : subtotal;
                return s + (base * parseFloat(d.value || 0) / 100);
            }
            return s + parseFloat(d.value || 0);
        }, 0);
        return Math.max(0, subtotal - discountSum);
    };
    const grandTotal = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);

    // ── Валидация ─────────────────────────────────────────────────
    const validation = useMemo(() => {
        const missing = [];
        lines.forEach((line, lineIdx) => {
            line.items.forEach((it, itIdx) => {
                if (!it.product_id) missing.push({ lineIdx, itemIdx: itIdx });
            });
        });
        return { isValid: !!phId && missing.length === 0, missing };
    }, [lines, phId]);

    // ── Создание чека ────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validation.isValid) return;
        setSubmitting(true);
        const payload = {
            photographer_id: phId,
            location_id: locationId,
            lines,
            comment: comment.trim() || undefined,
        };
        if (paymentNumberId) payload.payment_number_id = paymentNumberId;

        const res = await APICreateOrder(token, payload);
        setSubmitting(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось добавить чек'), 'danger');
            return;
        }
        const total = Array.isArray(res)
            ? res.reduce((s, o) => s + parseFloat(o.value || 0), 0)
            : parseFloat(res?.value || 0);
        showToast(`Чек добавлен · ${total.toLocaleString('ru-RU')} ₽`);
        if (onCreated) onCreated();
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={() => !submitting && onClose()}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 600, width: '100%',
                p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
                    <ReceiptLongRoundedIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                    Добавить чек
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                    Точка и админ смены подставятся автоматически
                </Typography>

                {/* Фотограф */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.5 }}>
                        Фотограф <Box component="span" sx={{ color: '#f87171' }}>*</Box>
                    </Typography>
                    <Select
                        size="sm"
                        value={phId}
                        onChange={(_, v) => setPhId(v)}
                        placeholder={loadingPh ? 'Загрузка…' : 'Выберите фотографа'}
                        sx={inputSx}
                        slotProps={{
                            listbox: {
                                sx: {
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                    '& .MuiOption-root': {
                                        color: 'var(--joy-palette-text-primary)',
                                        fontSize: 13,
                                        '&:hover': {
                                            bgcolor: 'rgba(162,38,226,0.12)',
                                            color: 'var(--joy-palette-text-primary)',
                                        },
                                        '&[aria-selected="true"]': {
                                            bgcolor: 'rgba(162,38,226,0.2)',
                                            color: 'var(--joy-palette-primary-300)',
                                            fontWeight: 600,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        {photographers.map(p => (
                            <Option key={p.id} value={p.id}>{p.full_name || p.first_name}</Option>
                        ))}
                    </Select>
                </Box>

                {/* Номер для оплаты (опционально) */}
                {paymentNumbers.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.5 }}>
                            <PhoneIphoneRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                            Номер для оплаты (опционально)
                        </Typography>
                        <Select
                            size="sm"
                            value={paymentNumberId}
                            onChange={(_, v) => setPaymentNumberId(v || '')}
                            placeholder="— не привязывать —"
                            sx={inputSx}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="">— не привязывать —</Option>
                            {paymentNumbers.map(pn => (
                                <Option key={pn.id} value={pn.id}>
                                    {pn.bank_display} · {pn.phone_formatted || pn.phone} · {pn.owner_name}
                                </Option>
                            ))}
                        </Select>
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            Номер привяжется только к строкам с переводом
                        </Typography>
                    </Box>
                )}

                {/* Строки оплаты */}
                <Stack spacing={2}>
                    {lines.map((line, lineIdx) => (
                        <Box key={lineIdx} sx={{
                            p: 1.5,
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-md)',
                            bgcolor: 'var(--joy-palette-background-level2)',
                        }}>
                            {/* Шапка строки */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                                <Stack direction="row" spacing={0.5}>
                                    {MONEY_TYPES.map(t => {
                                        const active = line.money_type === t.v;
                                        return (
                                            <IconButton key={t.v} size="sm" variant="plain"
                                                onClick={() => updateLine(lineIdx, 'money_type', t.v)}
                                                title={t.label}
                                                sx={{
                                                    minWidth: 32, minHeight: 32, borderRadius: 'var(--joy-radius-md)',
                                                    ...(active ? {
                                                        color: '#fff', bgcolor: t.c, border: `1px solid ${t.c}`,
                                                    } : {
                                                        color: t.c, bgcolor: 'transparent', border: `1px solid ${t.c}40`,
                                                    }),
                                                }}>
                                                {t.icon}
                                            </IconButton>
                                        );
                                    })}
                                </Stack>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Итог: <b style={{ color: 'var(--joy-palette-primary-300)' }}>{lineTotal(line).toLocaleString('ru-RU')} ₽</b>
                                    </Typography>
                                    {lines.length > 1 && (
                                        <IconButton size="sm" variant="plain" color="danger" onClick={() => removeLine(lineIdx)}>
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>

                            {/* Товары */}
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', mb: 0.5 }}>
                                <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Товары / услуги
                            </Typography>
                            <Stack spacing={0.8} sx={{ mb: 1 }}>
                                {line.items.map((it, itIdx) => (
                                    <Box key={itIdx} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Select size="sm"
                                            value={it.product_id ?? ''}
                                            onChange={(_, v) => pickProduct(lineIdx, itIdx, v || null)}
                                            placeholder="Выберите товар *"
                                            sx={{
                                                ...inputSx, flex: 2,
                                                ...(it.product_id ? {} : {
                                                    borderColor: '#f87171 !important',
                                                    bgcolor: 'rgba(248,113,113,0.05) !important',
                                                }),
                                            }}
                                            slotProps={{
                                                listbox: {
                                                    sx: {
                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                        '& .MuiOption-root': {
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontSize: 13,
                                                            '&:hover': {
                                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                                color: 'var(--joy-palette-text-primary)',
                                                            },
                                                            '&[aria-selected="true"]': {
                                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                                color: 'var(--joy-palette-primary-300)',
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {products.filter(p => p.is_active).map(p => (
                                                <Option key={p.id} value={p.id}>{p.name}</Option>
                                            ))}
                                        </Select>
                                        <Input size="sm" type="number"
                                            value={it.quantity}
                                            onChange={(e) => updateItem(lineIdx, itIdx, 'quantity', parseInt(e.target.value) || 1)}
                                            sx={{ ...inputSx, width: 60 }} />
                                        <Input size="sm" type="number"
                                            value={it.unit_price}
                                            onChange={(e) => updateItem(lineIdx, itIdx, 'unit_price', parseFloat(e.target.value) || 0)}
                                            sx={{ ...inputSx, width: 90 }}
                                            endDecorator={<Typography sx={{ fontSize: 10 }}>₽</Typography>} />
                                        {line.items.length > 1 && (
                                            <IconButton size="sm" variant="plain" color="danger" onClick={() => removeItem(lineIdx, itIdx)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                <Button size="sm" variant="plain"
                                    startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => addItem(lineIdx)}
                                    sx={{ alignSelf: 'flex-start', fontSize: 11, color: 'var(--joy-palette-primary-400)' }}>
                                    Добавить позицию
                                </Button>
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            {/* Скидки */}
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', mb: 0.5 }}>
                                <LocalOfferRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Скидки
                            </Typography>
                            <Stack spacing={0.8}>
                                {line.discounts.map((d, dIdx) => {
                                    const pickedProductIds = line.items.map(it => it.product_id).filter(Boolean);
                                    const availableDiscounts = discounts.filter(disc =>
                                        disc.is_active && (!disc.product || pickedProductIds.includes(disc.product))
                                    );
                                    return (
                                        <Box key={dIdx} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Select size="sm"
                                                value={d.discount_id ?? 'manual'}
                                                onChange={(_, v) => pickDiscount(lineIdx, dIdx, v === 'manual' ? null : v)}
                                                placeholder="Скидка" sx={{ ...inputSx, flex: 2 }}
                                                slotProps={{
                                                    listbox: {
                                                        sx: {
                                                            bgcolor: 'var(--joy-palette-background-level2)',
                                                            border: '1px solid var(--joy-palette-divider)',
                                                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                            '& .MuiOption-root': {
                                                                color: 'var(--joy-palette-text-primary)',
                                                                fontSize: 13,
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(162,38,226,0.12)',
                                                                    color: 'var(--joy-palette-text-primary)',
                                                                },
                                                                '&[aria-selected="true"]': {
                                                                    bgcolor: 'rgba(162,38,226,0.2)',
                                                                    color: 'var(--joy-palette-primary-300)',
                                                                    fontWeight: 600,
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <Option value="manual">— ручная —</Option>
                                                {availableDiscounts.map(disc => (
                                                    <Option key={disc.id} value={disc.id}>
                                                        {disc.name} ({disc.value_from}–{disc.value_to}{disc.is_percent ? '%' : '₽'})
                                                    </Option>
                                                ))}
                                            </Select>
                                            <Input size="sm" type="number"
                                                value={d.value}
                                                onChange={(e) => updateDiscount(lineIdx, dIdx, 'value', parseFloat(e.target.value) || 0)}
                                                sx={{ ...inputSx, width: 90 }}
                                                endDecorator={<Typography sx={{ fontSize: 10 }}>{d.is_percent ? '%' : '₽'}</Typography>} />
                                            <IconButton size="sm" variant="plain" color="danger" onClick={() => removeDiscount(lineIdx, dIdx)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                <Button size="sm" variant="plain"
                                    startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => addDiscount(lineIdx)}
                                    sx={{ alignSelf: 'flex-start', fontSize: 11, color: '#f59e0b' }}>
                                    Добавить скидку
                                </Button>
                            </Stack>
                        </Box>
                    ))}
                </Stack>

                <Button size="sm" variant="plain"
                    startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={addLine}
                    sx={{ mt: 1, fontSize: 11, color: 'var(--joy-palette-primary-400)' }}>
                    Добавить строку оплаты
                </Button>

                {/* Комментарий */}
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', mb: 0.5 }}>
                        Комментарий
                    </Typography>
                    <Textarea size="sm" minRows={2} maxRows={4}
                        placeholder="Имя клиента, флешка, кадры…"
                        value={comment}
                        onChange={(e) => setComment(e.target.value.slice(0, 200))}
                        sx={{ bgcolor: 'var(--joy-palette-background-level2)', fontSize: 13, color: 'white' }} />
                </Box>

                {/* Итог */}
                <Box sx={{
                    mt: 2, p: 1.5,
                    bgcolor: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 'var(--joy-radius-md)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Итого:</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#22c55e' }}>
                        {grandTotal.toLocaleString('ru-RU')} ₽
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={onClose} sx={{ flex: 1, borderColor: 'var(--joy-palette-divider)' }}>
                        Отмена
                    </Button>
                    <Button
                        loading={submitting}
                        disabled={!validation.isValid}
                        onClick={handleSubmit}
                        startDecorator={<AddRoundedIcon />}
                        sx={{
                            flex: 2,
                            background: validation.isValid
                                ? 'linear-gradient(135deg, #A226E2 0%, #c56ffa 100%)'
                                : 'var(--joy-palette-background-level2)',
                            color: '#fff', fontWeight: 800,
                            opacity: validation.isValid ? 1 : 0.5,
                        }}>
                        Добавить чек
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}